import React, { useState, useMemo } from 'react';
import { Box, Typography, Button, TextField, Radio, RadioGroup, FormControlLabel, Divider, Popover, Checkbox } from '@mui/material';
import { Search, ArrowDownAZ, ArrowUpAZ } from 'lucide-react';
import Toggle from '../ui/toggle';

interface CategoryNameCodeFilterProps {
    onApply?: (params: any) => void;
    onClear?: () => void;
    anchorEl?: HTMLElement | null;
    open?: boolean;
    onClose?: () => void;
    for: 'code' | 'name';
    state: { sortEnabled: boolean; filterEnabled: boolean; sortOrder: 'asc' | 'desc'; search: string; selectedCodes?: string[] };
    availableCodes?: string[];
}

const CategoryNameCodeFilter: React.FC<CategoryNameCodeFilterProps> = ({
    anchorEl, open, onClose, onApply, onClear,
    for: filterFor, state, availableCodes = [],
}) => {
    const [sortEnabled, setSortEnabled] = useState(state.sortEnabled);
    const [filterEnabled, setFilterEnabled] = useState(state.filterEnabled);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(state.sortOrder);
    const [search, setSearch] = useState(state.search);
    const [selectedCodes, setSelectedCodes] = useState<string[]>(state.selectedCodes ?? []);

    React.useEffect(() => {
        setSortEnabled(state.sortEnabled);
        setFilterEnabled(state.filterEnabled);
        setSortOrder(state.sortOrder);
        setSearch(state.search);
        setSelectedCodes(state.selectedCodes ?? []);
    }, [state]);

    const filteredCodes = useMemo(() =>
        availableCodes.filter(c => c.toLowerCase().includes(search.toLowerCase())),
        [availableCodes, search]
    );

    const allFilteredSelected = filteredCodes.length > 0 && filteredCodes.every(c => selectedCodes.includes(c));
    const someFilteredSelected = filteredCodes.some(c => selectedCodes.includes(c));

    const toggleCode = (code: string) => {
        setSelectedCodes(prev =>
            prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
        );
    };

    const toggleAll = () => {
        if (allFilteredSelected) {
            setSelectedCodes(prev => prev.filter(c => !filteredCodes.includes(c)));
        } else {
            setSelectedCodes(prev => Array.from(new Set([...prev, ...filteredCodes])));
        }
    };

    const handleApply = () => {
        onApply?.({ sortEnabled, filterEnabled, sortOrder, search, selectedCodes });
    };

    const handleClear = () => {
        setSortEnabled(false);
        setFilterEnabled(false);
        setSortOrder('asc');
        setSearch('');
        setSelectedCodes([]);
        onClear?.();
    };

    return (
        <Popover
            open={!!open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' } }}
        >
            <Box sx={{ p: 2, borderRadius: 3, background: 'var(--color-page-background)', width: 300 }}>
                <Typography fontWeight={600} fontSize={18} mb={1}>Sort & Filter</Typography>
                <Divider />

                {/* Sort */}
                <Box display="flex" alignItems="center" my={1}>
                    <Typography fontWeight={500} fontSize={15} ml={1}>
                        Sort By {filterFor === 'code' ? 'Code' : 'Name'}
                    </Typography>
                </Box>
                <Box ml={4} mb={1}>
                    <RadioGroup
                        value={sortEnabled ? sortOrder : ''}
                        onChange={e => { setSortOrder(e.target.value as 'asc' | 'desc'); setSortEnabled(true); }}
                    >
                        <FormControlLabel value="asc" control={<Radio color="primary" size="small" />}
                            label={<Box display="flex" alignItems="center" gap={1} fontSize={14}><ArrowDownAZ size={18} />{filterFor === 'code' ? 'Sort Ascending' : 'Sort A to Z'}</Box>}
                        />
                        <FormControlLabel value="desc" control={<Radio color="primary" size="small" />}
                            label={<Box display="flex" alignItems="center" gap={1} fontSize={14}><ArrowUpAZ size={18} />{filterFor === 'code' ? 'Sort Descending' : 'Sort Z to A'}</Box>}
                        />
                    </RadioGroup>
                </Box>
                <Divider />

                {/* Filter */}
                <Box display="flex" alignItems="center" my={1}>
                    <Toggle checked={filterEnabled} onChange={setFilterEnabled} />
                    <Typography fontWeight={500} fontSize={15} ml={1}>
                        Filter By {filterFor === 'code' ? 'Code' : 'Name'}
                    </Typography>
                </Box>

                {filterEnabled && filterFor === 'code' && availableCodes.length > 0 ? (
                    <Box mb={1}>
                        {/* Search within codes */}
                        <TextField
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search codes..."
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: <Search size={16} style={{ marginRight: 8, color: 'var(--color-primary)' }} />,
                                sx: { borderRadius: 2, background: 'var(--color-container-background)' },
                            }}
                            sx={{ mb: 1 }}
                        />

                        {/* Select All */}
                        <Box
                            display="flex" alignItems="center" px={0.5} py={0.25}
                            sx={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}
                            onClick={toggleAll}
                        >
                            <Checkbox
                                size="small"
                                checked={allFilteredSelected}
                                indeterminate={!allFilteredSelected && someFilteredSelected}
                                onChange={toggleAll}
                                onClick={e => e.stopPropagation()}
                                sx={{ p: 0.5, color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' }, '&.MuiCheckbox-indeterminate': { color: 'var(--color-primary)' } }}
                            />
                            <Typography fontSize={12} fontWeight={600} color="var(--color-primary)">Select All</Typography>
                            {selectedCodes.length > 0 && (
                                <Typography fontSize={11} color="var(--color-text-muted)" ml="auto">{selectedCodes.length} selected</Typography>
                            )}
                        </Box>

                        {/* Code list */}
                        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                            {filteredCodes.map(code => (
                                <Box
                                    key={code}
                                    display="flex" alignItems="center" px={0.5}
                                    sx={{ cursor: 'pointer', '&:hover': { background: 'var(--color-container-background)' }, borderBottom: '1px solid #f5f5f5' }}
                                    onClick={() => toggleCode(code)}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={selectedCodes.includes(code)}
                                        onChange={() => toggleCode(code)}
                                        onClick={e => e.stopPropagation()}
                                        sx={{ p: 0.5, color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' } }}
                                    />
                                    <Typography fontSize={13}>{code}</Typography>
                                </Box>
                            ))}
                            {filteredCodes.length === 0 && (
                                <Typography fontSize={12} color="text.secondary" sx={{ px: 2, py: 1 }}>No codes found</Typography>
                            )}
                        </Box>
                    </Box>
                ) : filterEnabled ? (
                    <Box mb={2}>
                        <TextField
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: <Search size={18} style={{ marginRight: 8, color: 'var(--color-primary)' }} />,
                                sx: { borderRadius: 2, background: 'var(--color-container-background)' },
                            }}
                        />
                    </Box>
                ) : null}

                <Box display="flex" gap={2} mt={2}>
                    <Button
                        variant="contained"
                        sx={{ flex: 1, borderRadius: 2, background: 'var(--color-primary)', color: '#fff', fontWeight: 600, boxShadow: 'none', textTransform: 'none', '&:hover': { background: 'var(--color-primary)' } }}
                        onClick={handleApply}
                    >
                        Apply Now
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{ flex: 1, borderRadius: 2, fontWeight: 600, background: 'var(--color-container-background)', color: 'var(--color-primary)', borderColor: 'var(--color-primary)', boxShadow: 'none', textTransform: 'none', '&:hover': { background: 'var(--color-container-background)', borderColor: 'var(--color-primary)' } }}
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                </Box>
            </Box>
        </Popover>
    );
};

export default CategoryNameCodeFilter;
